import { useCallback, useState } from 'react';

export interface FiltrosPaginacao {
  [chave: string]: string | number | boolean | undefined;
}

interface RespostaPaginada {
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

/**
 * Estado de paginação + filtros (padrão do projeto — ver skill arquiteto-sistema).
 * Trocar filtro ou limite reseta para a página 1; `syncFromResponse` atualiza os totais.
 */
export function usePagination({
  defaultLimit = 20,
  defaultFilters = {},
}: { defaultLimit?: number; defaultFilters?: FiltrosPaginacao } = {}) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [filters, setFilters] = useState<FiltrosPaginacao>(defaultFilters);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    for (const [chave, valor] of Object.entries(filters)) {
      if (valor !== undefined && valor !== '') {
        params.set(chave, String(valor));
      }
    }
    return params;
  }, [page, limit, filters]);

  const syncFromResponse = useCallback((res: RespostaPaginada) => {
    setTotalItems(res.pagination?.total ?? 0);
    setTotalPages(res.pagination?.totalPages ?? 1);
  }, []);

  const mudarPage = useCallback((novaPagina: number) => {
    setPage(Math.max(1, novaPagina));
  }, []);

  const mudarLimit = useCallback((novoLimite: number) => {
    setLimit(Math.max(1, novoLimite));
    setPage(1);
  }, []);

  const mudarFilters = useCallback((novosFiltros: FiltrosPaginacao) => {
    setFilters(novosFiltros);
    setPage(1);
  }, []);

  const resetPagination = useCallback(() => {
    setPage(1);
    setLimit(defaultLimit);
    setFilters(defaultFilters);
    setTotalItems(0);
    setTotalPages(1);
  }, [defaultLimit, defaultFilters]);

  return {
    page,
    limit,
    filters,
    totalItems,
    totalPages,
    setPage: mudarPage,
    setLimit: mudarLimit,
    setFilters: mudarFilters,
    buildQueryParams,
    syncFromResponse,
    resetPagination,
  };
}